import { toTrimmedNonEmptyString } from '../../lib/utils/type-guards';
import type { Message } from './chat-types';

type MessagePayload = { role: Message['role']; content: string };

export const normalizeMessage = (message: Message): Message => message;

interface ModelAnnouncement {
  canonicalModelId: string;
  displayName: string;
  provider: string;
  providerModelId: string;
}

const MODEL_HEADER_KEYS = {
  providerId: 'x-chat-model-provider-id',
  canonicalId: 'x-chat-model-canonical-id',
  provider: 'x-chat-model-provider',
  displayName: 'x-chat-model-display-name',
} as const;

export const extractModelAnnouncement = (response: Response): ModelAnnouncement | null => {
  const providerModelId = toTrimmedNonEmptyString(response.headers.get(MODEL_HEADER_KEYS.providerId));
  if (!providerModelId) {
    return null;
  }
  const canonicalModelId =
    toTrimmedNonEmptyString(response.headers.get(MODEL_HEADER_KEYS.canonicalId)) ?? providerModelId;
  const provider =
    toTrimmedNonEmptyString(response.headers.get(MODEL_HEADER_KEYS.provider)) ??
    providerModelId.split('/')[0] ??
    'unknown';
  const displayName = toTrimmedNonEmptyString(response.headers.get(MODEL_HEADER_KEYS.displayName)) ?? canonicalModelId;
  return {
    providerModelId,
    canonicalModelId,
    provider,
    displayName,
  } satisfies ModelAnnouncement;
};

export const createDefaultWelcomeMessage = (): Message => ({
  id: 'welcome',
  role: 'assistant',
  content:
    "🦉 Hey there! I'm the Sweetistics Owl. I'm connecting to the bird network right now so we can pull in your home timeline, mentions, likes, and bookmarks. I'll keep you posted as the import jobs finish and call out highlights as they arrive.\n\nIf you want, tell me what kind of insights you care about—daily digests, pulse alerts, or anything else—and I'll make sure it happens.",
  timestamp: new Date(),
});

export const buildMessagesPayload = (history: Message[]): MessagePayload[] => {
  const payload: MessagePayload[] = [];
  for (const message of history) {
    if (message.id === 'welcome') {
      continue;
    }
    if (message.id.startsWith('model-change-')) {
      continue;
    }
    if (typeof message.content !== 'string' || !toTrimmedNonEmptyString(message.content)) {
      continue;
    }
    payload.push({ role: message.role, content: message.content });
  }
  return payload;
};

export type StreamingLogger = {
  info: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  debug: (message: string, data?: unknown) => void;
};
